"use client";

import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { Check, ExternalLink, MessageCircleMore, Users2 } from "lucide-react";
import type { Departure, PackagePricing, PackageAddon, Media } from "@/lib/dto";
import { formatDate, formatPrice } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/format";
import { SectionHeading } from "../ui/SectionHeading";
import { BookingWizard } from "@/components/trip/BookingWizard";
import { InquiryDialog } from "@/components/trip/InquiryDialog";
import {
  MonthYearFilter,
  type MonthOption,
} from "@/components/trip/MonthYearFilter";

// Lazy-load the calendar (react-day-picker + date-fns, ~90KB) — it's only
// rendered when a trip has no fixed departures, so it stays out of the
// initial bundle for every package page with a scheduled-departures table.
const DayPicker = dynamic(
  () => import("@/components/home/LazyDayPicker").then((m) => m.DayPicker),
  {
    ssr: false,
    loading: () => (
      <div className="custom-date-picker grid min-h-[18rem] place-items-center text-sm text-muted-soft">
        Loading calendar…
      </div>
    ),
  },
);

/**
 * Scheduled-departures panel: a year selector, a month rail (shared with
 * TierBookingSidebar via `MonthYearFilter`), and a list/table of departures
 * for the active month (date range, status, price, Book/Date Inquiry). Data
 * is server-fetched and passed in; the year/month grouping + active-tab is a
 * light client island (no extra requests) — same grouping logic as
 * `TierBookingSidebar.tsx`, so both surfaces read departures identically.
 */

type Props = {
  departures: Departure[];
  packageSlug: string;
  packageTitle: string;
  packageId?: number;
  currency?: string;
  /** Trip length, used to show duration + compute the private end date. */
  duration?: number;
  durationUnit?: string;
  pricing?: PackagePricing;
  addons?: PackageAddon[];
  featuredImage?: Media | null;
  hidden?: boolean;
  /** Rich-text booking/pricing policy note shown above the departure list. */
  departure_note?: string | null;
};

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const SHORT_DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});
const BADGE_MONTH_FMT = new Intl.DateTimeFormat("en-US", { month: "short" });
const BADGE_DAY_FMT = new Intl.DateTimeFormat("en-US", { day: "2-digit" });

function safeDate(value: string): Date | null {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatRange(start: string, end: string): string {
  const s = safeDate(start);
  const e = safeDate(end);
  if (!s) return "";
  if (!e) return DATE_FMT.format(s);
  const sameYear = s.getFullYear() === e.getFullYear();
  return sameYear
    ? `${SHORT_DATE_FMT.format(s)} – ${DATE_FMT.format(e)}`
    : `${DATE_FMT.format(s)} – ${DATE_FMT.format(e)}`;
}

export function statusLabel(d: Departure): {
  label: string;
  tone: "amber" | "navy" | "teal";
} {
  const status = (d.status || "").toLowerCase();
  if (status === "sold_out" || d.spotsAvailable === 0)
    return { label: "Sold out", tone: "amber" };
  if (d.spotsAvailable != null && d.spotsAvailable <= 3) {
    return { label: `${d.spotsAvailable} left`, tone: "amber" };
  }
  if (status === "guaranteed") return { label: "Guaranteed", tone: "teal" };
  if (status === "almost_full") return { label: "Almost full", tone: "amber" };
  return { label: "Available", tone: "navy" };
}

function inquiryMessage(d: Departure, packageTitle: string): string {
  const start = formatDate(d.startDate);
  const end = formatDate(d.endDate);
  const range =
    end && end !== start ? `${start} and returning on ${end}` : start;
  return `Hi, I'm interested in departing on ${range} for ${packageTitle}. Can you provide more details?`;
}

export function DeparturesPanel({
  departures,
  packageSlug,
  packageTitle,
  packageId,
  currency = "USD",
  duration,
  durationUnit,
  hidden,
  pricing,
  addons,
  featuredImage,
  departure_note,
}: Props) {
  const months = useMemo<MonthOption[]>(() => {
    const grouped = new Map<string, MonthOption>();
    for (const d of departures) {
      const s = safeDate(d.startDate);
      if (!s) continue;
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, "0")}`;
      const current = grouped.get(key);
      if (current) {
        current.count += 1;
      } else {
        grouped.set(key, {
          key,
          year: s.getFullYear(),
          month: s.getMonth(),
          label: key,
          count: 1,
        });
      }
    }
    return [...grouped.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [departures]);

  const [activeMonthKey, setActiveMonthKey] = useState<string>(
    months[0]?.key ?? "",
  );
  const activeMonth = months.find((m) => m.key === activeMonthKey) ?? months[0];
  const years = useMemo(
    () => [...new Set(months.map((m) => m.year))].sort((a, b) => a - b),
    [months],
  );
  const visibleDepartures = useMemo(() => {
    if (!activeMonth) return [];
    return departures.filter((d) => {
      const s = safeDate(d.startDate);
      return (
        s?.getFullYear() === activeMonth.year &&
        s.getMonth() === activeMonth.month
      );
    });
  }, [activeMonth, departures]);

  if (hidden) return null;

  return (
    <section aria-labelledby="dates-heading">
      <SectionHeading title="Dates & Availability" />

      {departure_note ? (
        <div className="bg-[#EFF0F1] mt-6 p-6 rounded-md text-md font-secondary">
          <h3 className="mb-3 text-[1.125rem] font-display font-extrabold">
            Note to Travelers
            <MessageCircleMore
              fill="#14BF60"
              stroke="currentColor"
              strokeWidth={1.5}
              size={20}
              className="align-text-bottom ml-1 inline-block"
              aria-hidden
            />{" "}
          </h3>
          <article
            dangerouslySetInnerHTML={{ __html: departure_note }}
            className="text-text [&_p+p]:mt-3 leading-[1.6]"
          ></article>
        </div>
      ) : null}

      {departures.length === 0 || !activeMonth ? (
        <PrivateDatePicker
          packageTitle={packageTitle}
          packageSlug={packageSlug}
          packageId={packageId}
          currency={currency}
          duration={duration}
          durationUnit={durationUnit}
          pricing={pricing}
          addons={addons}
          featuredImage={featuredImage}
        />
      ) : (
        <>
          {/* Year tabs */}
          {years.length > 1 ? (
            <div
              className="flex gap-2 mt-6 rounded-full bg-bg"
              role="tablist"
              aria-label="Departure year"
            >
              {years.map((year) => {
                const selected = year === activeMonth.year;
                return (
                  <button
                    key={year}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      const firstMonth = months.find((m) => m.year === year);
                      if (firstMonth) setActiveMonthKey(firstMonth.key);
                    }}
                    className={cn(
                      "shrink-0 font-extrabold shadow-sm border ease-in transition-all delay-75 duration-100 relative  px-4 py-1.5 text-xs before:absolute before:bottom-[-1px] before:left-[calc(45%+1px)] before:w-[9px] before:h-[2px] before:bg-[#ACE6C0] before:z-[2] after:absolute after:block after:bottom-[-5px] after:left-[45%] after:w-[11px] after:h-[11px] after:bg-[#ACE6C0] after:rotate-45 after:border after:border-headings after:z-[-1]",
                      selected
                        ? "bg-[#ACE6C0] text-headings  border-black rounded before:opacity-100 after:opacity-100 before:translate-0 after:translate-0"
                        : "text-muted hover:text-headings rounded-full before:opacity-0 after:opacity-0 before:translate-3 after:translate-3 border-black/30",
                    )}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* Month tabs */}
          <MonthYearFilter
            months={months}
            activeKey={activeMonthKey}
            onChange={setActiveMonthKey}
            className="mt-4 border-b border-line"
          />

          {/* Mobile: card list. Desktop: table. */}
          <div className="mt-6 space-y-3 md:hidden">
            {visibleDepartures.map((d) => {
              const depCurrency = d.currency || currency;
              const price = formatPrice(d.currentPrice, depCurrency);
              const wasPrice =
                d.wasPrice && d.currentPrice && d.wasPrice > d.currentPrice
                  ? formatPrice(d.wasPrice, depCurrency)
                  : null;
              const { label, tone } = statusLabel(d);
              const sold = label === "Sold out";
              const startDateObj = new Date(d.startDate);

              return (
                <div
                  key={d.id}
                  className="flex items-center gap-3 rounded-xl border border-line bg-paper px-4 py-3"
                >
                  <DepartureDateBadge date={startDateObj} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-headings">
                      {formatRange(d.startDate, d.endDate)}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <Badge variant={tone}>{label}</Badge>
                      {!sold && d.spotsAvailable != null ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-soft">
                          <Users2 size={10} aria-hidden />
                          {d.spotsAvailable} spots left
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-sm font-display text-headings">
                      {wasPrice ? (
                        <span className="mr-1 text-xs line-through text-muted-soft">
                          {wasPrice}
                        </span>
                      ) : null}
                      {price || "—"}
                    </div>
                    {d.note ? (
                      <p className="mt-0.5 text-[11px] text-muted-soft">
                        {d.note}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {sold ? (
                      <span className="text-xs font-medium text-muted-soft">
                        Sold out
                      </span>
                    ) : (
                      <BookingWizard
                        packageTitle={packageTitle}
                        packageSlug={packageSlug}
                        packageId={packageId ?? d.packageId}
                        departureId={d.id}
                        currency={depCurrency}
                        startDate={startDateObj}
                        pricing={pricing}
                        addons={addons}
                        featuredImage={featuredImage}
                        duration={duration}
                        durationUnit={durationUnit}
                        trigger={
                          <button
                            type="button"
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-primary px-3.5 py-1.5 text-xs font-bold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white"
                          >
                            Book
                          </button>
                        }
                      />
                    )}
                    <DateInquiryTrigger
                      packageTitle={packageTitle}
                      packageSlug={packageSlug}
                      departure={d}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 hidden bg-white md:block overflow-hidden rounded-md border border-line">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-border/50 text-left text-[11px] uppercase tracking-wider text-muted-soft">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Departure &amp; return
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 font-semibold text-right"
                  >
                    Price
                  </th>
                  <th scope="col" className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {visibleDepartures.map((d) => {
                  const depCurrency = d.currency || currency;
                  const price = formatPrice(d.currentPrice, depCurrency);
                  const wasPrice =
                    d.wasPrice && d.currentPrice && d.wasPrice > d.currentPrice
                      ? formatPrice(d.wasPrice, depCurrency)
                      : null;
                  const { label, tone } = statusLabel(d);
                  const sold = label === "Sold out";
                  const startDateObj = new Date(d.startDate);

                  return (
                    <tr key={d.id} className="hover:bg-bg/60">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <DepartureDateBadge date={startDateObj} />
                          <div className="min-w-0">
                            <div className="font-bold font-display text-headings">
                              {formatRange(d.startDate, d.endDate)}
                            </div>
                            {/* {duration ? (
                              <p className="mt-0.5 text-xs text-muted-soft">
                                {duration} {durationUnit || "Days"} Adventure
                              </p>
                            ) : null} */}
                            {d.note ? (
                              <p className="mt-0.5 text-xs text-muted-soft">
                                {d.note}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className=" text-xs font-medium text-headings tracking-normal capitalize">
                          <Check
                            size={15}
                            strokeWidth="4px"
                            className="bg-primary/30 rounded-full text-black p-[3px]"
                          />
                          {label}
                        </Badge>
                        {!sold && d.spotsAvailable != null ? (
                          <span className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-muted-soft">
                            <Users2 size={10} aria-hidden />
                            {d.spotsAvailable} spots left
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {wasPrice ? (
                          <span className="mr-1 text-xs line-through text-muted-soft">
                            {wasPrice}
                          </span>
                        ) : null}
                        <span className="font-display text-base font-bold text-headings">
                          {price || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-end gap-1.5">
                          {sold ? (
                            <Button
                              variant="ghost"
                              disabled
                              className="opacity-50 !cursor-not-allowed"
                            >
                              Sold out
                            </Button>
                          ) : (
                            <BookingWizard
                              packageTitle={packageTitle}
                              packageSlug={packageSlug}
                              packageId={packageId ?? d.packageId}
                              departureId={d.id}
                              currency={depCurrency}
                              startDate={startDateObj}
                              pricing={pricing}
                              addons={addons}
                              featuredImage={featuredImage}
                              duration={duration}
                              durationUnit={durationUnit}
                              trigger={
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center bg-primary/5 whitespace-nowrap rounded-md font-display border border-primary/50 px-4 py-1.5 text-xs font-extrabold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white"
                                >
                                  Book Now
                                </button>
                              }
                            />
                          )}
                          <DateInquiryTrigger
                            packageTitle={packageTitle}
                            packageSlug={packageSlug}
                            departure={d}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="mt-4 text-xs text-muted-soft">
        All prices per person, twin share. Subject to availability —{" "}
        {packageTitle} dates fill quickly in peak season.
      </p>
    </section>
  );
}

/** Small rounded date badge — month abbreviation over the day number. */
function DepartureDateBadge({ date }: { date: Date }) {
  return (
    <div className="flex scale-90 relative h-12 w-12 shrink-0 flex-col gap-y-0.5 items-center justify-center   text-black shadow-sm before:absolute before:inset-y-1 before:inset-x-0.5 before:bg-primary/50 z-10 before:-z-10">
      <span className="text-base pt-1 font-display font-black leading-none">
        {BADGE_DAY_FMT.format(date)}
      </span>
      <span className="text-[10px] pt-0.5 font-black uppercase leading-none">
        {BADGE_MONTH_FMT.format(date)}
      </span>
      <svg
        height={48}
        width={48}
        className="h-full w-full opacity-50 inset-0 absolute"
      >
        <use xlinkHref="/icons.svg#date" />
      </svg>
    </div>
  );
}

/** "Date Inquiry" trigger — opens the shared InquiryDialog prefilled with the
 *  specific departure's dates, matching the reference prototype's per-date
 *  enquiry popup. */
function DateInquiryTrigger({
  packageTitle,
  packageSlug,
  departure,
}: {
  packageTitle: string;
  packageSlug: string;
  departure: Departure;
}) {
  return (
    <InquiryDialog
      title="Date Inquiry"
      dialogTitle={`Date Inquiry — ${packageTitle}`}
      slug={packageSlug}
      defaultSubject={`Date Inquiry: ${packageTitle}`}
      defaultMessage={inquiryMessage(departure, packageTitle)}
      trigger={
        <button
          type="button"
          className="inline-flex text-muted items-center gap-1 text-xxs font-medium hover:text-primary  hover:underline"
        >
          Date Inquiry{" "}
          <ExternalLink size={12} aria-hidden className="text-primary" />
        </button>
      }
    />
  );
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Shown when a trip has no fixed group departures: a two-month inline calendar
 * to pick a private start date, which routes into the booking flow via
 * BookingWizard. Matches the "Planning your trip schedule" design concept and
 * reuses the shared react-day-picker skin (`custom-date-picker`,
 * styles/ui/date-picker.css) so it looks identical to the TripPlanner picker —
 * month/year dropdowns, prev/next nav, and strikethrough sold-out (past) days.
 */
function PrivateDatePicker({
  packageTitle,
  packageSlug,
  packageId,
  currency,
  duration,
  durationUnit,
  pricing,
  addons,
  featuredImage,
}: {
  packageTitle: string;
  packageSlug: string;
  packageId?: number;
  currency: string;
  duration?: number;
  durationUnit?: string;
  pricing?: PackagePricing;
  addons?: PackageAddon[];
  featuredImage?: Media | null;
}) {
  // Earliest selectable date: one week out (matches the prototype's lead time).
  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [selected, setSelected] = useState<Date>();

  const unit = (durationUnit || "days").toLowerCase();
  const isDays = unit.startsWith("day");
  // Trip span for the "Departure & Return" hint (days only; hours = same day).
  const endDate =
    selected && isDays && duration && duration > 1
      ? addDays(selected, duration - 1)
      : selected;

  return (
    <div className="surface-card mt-6 overflow-hidden p-4 sm:p-8">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <svg
          className="lg:h-9 flex-[0_0_28px] w-7 lg:flex-[0_0_36px] h-7 mr-2 lg:w-9 inline-block text-muted"
          aria-hidden
        >
          <use xlinkHref="/icons.svg#calendar" />
        </svg>
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-lg sm:text-2xl">
            Select Departure Date
          </h3>
          <p className="text-sm leading-snug text-muted-soft pt-1">
            No fixed group departures right now — run {packageTitle} privately
            with your own group. Pick a preferred start date and we&apos;ll
            confirm availability.
          </p>
        </div>
      </div>
      {/* Calendar — same skin as the TripPlanner picker */}
      <div className="custom-date-popover mt-5 rounded-md !bg-transparent !backdrop-blur-none !border-line">
        <DayPicker
          className="custom-date-picker"
          mode="single"
          captionLayout="dropdown"
          selected={selected}
          onSelect={setSelected}
          disabled={{ before: minDate }}
          startMonth={minDate}
          endMonth={addDays(minDate, 365 * 3)}
          numberOfMonths={
            typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 2
          }
          defaultMonth={minDate}
        />
      </div>
      {/* Legend + footer */}
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <span className="flex items-center gap-2 text-xs text-muted-soft">
          <span className="inline-block h-3 w-3 rounded-full bg-bg ring-1 ring-line" />
          Sold out dates
        </span>
        <p className="text-xs italic text-muted-soft">
          Group-size discounts will be applied in the next step.
        </p>
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted">
          {selected ? (
            <>
              Selected start:{" "}
              <span className="font-medium text-headings">
                {DATE_FMT.format(selected)}
                {endDate && endDate !== selected
                  ? ` – ${DATE_FMT.format(endDate)}`
                  : ""}
              </span>
            </>
          ) : (
            "Select a start date from the calendar."
          )}
        </p>
        {selected ? (
          <BookingWizard
            packageTitle={packageTitle}
            packageSlug={packageSlug}
            packageId={packageId}
            currency={currency}
            startDate={selected}
            pricing={pricing}
            addons={addons}
            featuredImage={featuredImage}
            duration={duration}
            durationUnit={durationUnit}
            trigger={
              <Button
                variant="primary"
                arrow
                className="justify-center sm:w-fit"
              >
                Continue booking
              </Button>
            }
          />
        ) : (
          <Button
            variant="primary"
            disabled
            arrow
            className="justify-center sm:w-fit"
          >
            Select dates
          </Button>
        )}
      </div>
    </div>
  );
}
